<template>
  <nav-header :title="title"></nav-header>
  <main class="statistics-page is-finn-statistics">
    <div class="container statistics-container statistics-stack finn-area-statistics">
      <div class="statistics-note">
        <i class="material-icons" aria-hidden="true">schedule</i>
        <p>
          {{
            $englishPreferred
              ? "Data on this page is calculated nightly. The data can be up to one day old."
              : "Data fremvist på denne side udregnes hver nat, data kan derfor være op til et døgn bagud."
          }}
        </p>
      </div>

      <section class="statistics-panel">
      <div class="finn-area-filter">
      <label class="form-label" for="finn-area-filter">
        {{
          $englishPreferred
            ? "Filter by FiNN area"
            : "Filtrer efter FiNN-område"
        }}
      </label>
      <input
        id="finn-area-filter"
        v-model.trim="searchTerm"
        class="form-control"
        type="text"
        :placeholder="
          $englishPreferred
            ? 'Search by area name'
            : 'Søg på områdenavn'
        "
      />
    </div>

    <div v-if="sortedStatistics.length > 0" class="table-responsive">
      <table class="table table-striped table-sm align-middle finn-area-table">
        <colgroup>
          <col class="name-column" />
          <col class="count-column" />
          <col class="count-column" />
          <col class="count-column" />
        </colgroup>
        <thead class="table-primary">
          <tr>
            <th scope="col">
              <button
                type="button"
                class="table-sort-button"
                @click="setSort('title')"
              >
                {{ $englishPreferred ? "Name" : "Navn" }}
                <span>{{ getSortIndicator("title") }}</span>
              </button>
            </th>
            <th scope="col" class="text-end">
              <button
                type="button"
                class="table-sort-button justify-content-end table-sort-button-nowrap"
                @click="setSort('totalCounts30Days')"
              >
                {{ $englishPreferred ? "30 days" : "30 dage" }}
                <span>{{ getSortIndicator("totalCounts30Days") }}</span>
              </button>
            </th>
            <th scope="col" class="text-end">
              <button
                type="button"
                class="table-sort-button justify-content-end"
                @click="setSort('totalCountsCurrentYear')"
              >
                {{ currentYear }}
                <span>{{ getSortIndicator("totalCountsCurrentYear") }}</span>
              </button>
            </th>
            <th scope="col" class="text-end">
              <button
                type="button"
                class="table-sort-button justify-content-end table-sort-button-nowrap"
                @click="setSort('totalCounts')"
              >
                {{ $englishPreferred ? "All time" : "I alt" }}
                <span>{{ getSortIndicator("totalCounts") }}</span>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="siteStatistic in sortedStatistics"
            :key="siteStatistic.finnSiteId ?? siteStatistic.title"
          >
            <td class="site-name-cell">{{ siteStatistic.title }}</td>
            <td class="text-end">{{ siteStatistic.totalCounts30Days }}</td>
            <td class="text-end">{{ siteStatistic.totalCountsCurrentYear }}</td>
            <td class="text-end">{{ siteStatistic.totalCounts }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="isLoading">
      {{
        $englishPreferred
          ? "Fetching area statistics"
          : "Henter områdestatistik"
      }}...
    </div>
    <div v-else-if="!online">
      {{
        $englishPreferred
          ? "Could not fetch area statistics - maybe you are offline?"
          : "Kunne ikke hente områdestatistik - måske er du offline?"
      }}
    </div>
    <div v-else>
      {{
        $englishPreferred
          ? "No area statistics found."
          : "Ingen områdestatistik fundet."
      }}
    </div>
      </section>
    </div>
  </main>
</template>

<script>
import NavHeader from "@/components/navbars/NavHeader.vue";
import axios from "axios";

export default {
  components: {
    NavHeader,
  },
  data() {
    return {
      title: this.$englishPreferred
        ? "FiNN area statistics"
        : "FiNN-områdestatistik",
      currentYear: new Date().getFullYear(),
      isLoading: true,
      online: true,
      searchTerm: "",
      sortKey: "totalCounts",
      sortDirection: "desc",
      siteStatistics: [],
    };
  },
  computed: {
    filteredStatistics() {
      const normalizedSearchTerm = this.searchTerm.toLowerCase();

      if (!normalizedSearchTerm) {
        return this.siteStatistics;
      }

      return this.siteStatistics.filter((siteStatistic) =>
        (siteStatistic.title || "").toLowerCase().includes(normalizedSearchTerm)
      );
    },
    sortedStatistics() {
      const sortedStatistics = [...this.filteredStatistics];
      const directionFactor = this.sortDirection === "asc" ? 1 : -1;

      sortedStatistics.sort((siteStatisticA, siteStatisticB) => {
        const valueA = siteStatisticA[this.sortKey];
        const valueB = siteStatisticB[this.sortKey];

        if (this.sortKey === "title") {
          return (
            (valueA || "").localeCompare(valueB || "", "da", {
              sensitivity: "base",
            }) * directionFactor
          );
        }

        if (valueA === valueB) {
          return (siteStatisticA.title || "").localeCompare(
            siteStatisticB.title || "",
            "da",
            {
              sensitivity: "base",
            }
          );
        }

        return ((valueA || 0) - (valueB || 0)) * directionFactor;
      });

      return sortedStatistics;
    },
  },
  methods: {
    async getFinnAreaStatistics() {
      return axios.get("/spontan/statistics/sites/finn");
    },
    setSort(sortKey) {
      if (this.sortKey === sortKey) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
        return;
      }

      this.sortKey = sortKey;
      this.sortDirection = sortKey === "title" ? "asc" : "desc";
    },
    getSortIndicator(sortKey) {
      if (this.sortKey !== sortKey) {
        return "↕";
      }

      return this.sortDirection === "asc" ? "↑" : "↓";
    },
    async prepareData() {
      this.isLoading = true;

      await this.getFinnAreaStatistics()
        .then((response) => {
          this.siteStatistics = response.data || [];
        })
        .catch(() => {
          this.online = false;
        });

      this.isLoading = false;
    },
  },
  mounted() {
    this.prepareData();
  },
};
</script>

<style scoped>
:deep(#navbar) {
  margin-bottom: 0 !important;
}

.finn-area-statistics {
  padding-bottom: 2rem;
}

.finn-area-filter {
  margin-bottom: 1rem;
}

.finn-area-filter label {
  color: #174f70;
  font-weight: 700;
}

.finn-area-filter input {
  border: 1px solid rgb(31 122 175 / 20%);
  border-radius: 0.8rem;
  min-height: 3rem;
}

.table-responsive {
  border: 1px solid rgb(31 122 175 / 10%);
  border-radius: 0.9rem;
  overflow: hidden;
}

.finn-area-table {
  margin-bottom: 0;
}

.finn-area-table thead {
  background: rgb(31 122 175 / 10%);
  color: #174f70;
}

.finn-area-table th,
.finn-area-table td {
  padding: 0.7rem;
}

.table-sort-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font-weight: 700;
}

.finn-area-table {
  table-layout: fixed;
}

.finn-area-table .name-column {
  width: 34%;
}

.finn-area-table .count-column {
  width: 22%;
}

.site-name-cell {
  word-break: break-word;
}

.table-sort-button-nowrap {
  white-space: nowrap;
}
</style>
